// Real HTTP adapter for the password-reset flow (login itself is handled by api/auth.ts authenticate()).
// Maps the server error envelope { error: { code, message, fields } } to ResetApiError.
// Activated by VITE_USE_MOCK_API=false; see login.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  ResetApiError,
  type ChangePasswordInput,
  type ForcedChangePasswordInput,
  type LoginApi,
  type ResetApiErrorCode,
  type SendResetCodeInput,
  type VerifyResetCodeInput,
  type VerifyResetCodeResult,
} from '../types/login.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapResetError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new ResetApiError(
    (payload?.code as ResetApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields },
  );
}

export const loginHttpApi: LoginApi = {
  // request-otp. An unregistered email surfaces as EMAIL_NOT_REGISTERED (mapped from the BE
  // envelope by mapResetError); the page keeps the user on step 1 with the spec message.
  async sendResetCode(input: SendResetCodeInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.passwordResetRequestOtp, {
        email: input.email,
      });
    } catch (e) {
      mapResetError(e);
    }
  },

  // verify-otp → 200 { data: { resetToken } }. BE field is `otp`; the FE collects it as `code`.
  async verifyResetCode(input: VerifyResetCodeInput): Promise<VerifyResetCodeResult> {
    try {
      const res = await httpClient.post<ApiEnvelope<VerifyResetCodeResult>>(
        endpoints.auth.passwordResetVerifyOtp,
        { email: input.email, otp: input.code },
      );
      return res.data.data;
    } catch (e) {
      mapResetError(e);
    }
  },

  // complete (forgot-password mode) → 204 no body.
  async changePassword(input: ChangePasswordInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.passwordResetComplete, {
        resetToken: input.resetToken,
        newPassword: input.newPassword,
        confirmPassword: input.confirmPassword,
      });
    } catch (e) {
      mapResetError(e);
    }
  },

  // forced-change (forced post-login mode) → 204 no body. No resetToken — the BE resolves the
  // user from the bearer (dedicated forced endpoint; see api-contracts/login.md §2.6).
  async forcedChangePassword(input: ForcedChangePasswordInput): Promise<void> {
    try {
      await httpClient.post(endpoints.auth.passwordResetForcedChange, {
        newPassword: input.newPassword,
        confirmPassword: input.confirmPassword,
      });
    } catch (e) {
      mapResetError(e);
    }
  },
};
